import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { UiActionBarComponent } from '../action-bar/ui-action-bar.component';
import { UiIllustrationComponent } from '../illustration/ui-illustration.component';

/** One step of a ui-stepper (5.1). */
export interface UiStepperStep {
  readonly key: string;
  readonly title: string;
  /** Group name shown before "Step N of M" (e.g. the Application's groups). */
  readonly group?: string;
  /** Kit illustration name shown in the section head. */
  readonly illustration?: string;
  /**
   * The host owns the state: done = green segment, error = red segment,
   * unfinished = amber (U12 round 3: changed but not saved, or saved but
   * incomplete — browsing must never look like a failure).
   */
  readonly state: 'todo' | 'done' | 'error' | 'unfinished';
}

/**
 * rebuild-enrollment-intake 5.1 (DCMPDEV-738 DCM-366): the reusable step
 * shell — the section head ("<group> · Step N of M", title, illustration),
 * a clickable segment bar (one segment per step, classes ok / cur / err,
 * click emits `jump` with the index) and the action bar (Back hidden on
 * step 0, the primary button shows `lastLabel` on the last step, both
 * disabled while `busy`). The step body is projected via ng-content.
 *
 * The stepper never saves anything: the host owns the step states and
 * decides when to move `active` (after a successful save), by reacting to
 * the `next` / `back` / `jump` outputs.
 */
@Component({
  selector: 'ui-stepper',
  imports: [MatButton, UiActionBarComponent, UiIllustrationComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (step(); as s) {
      <div class="sec-head">
        @if (s.illustration) {
          <ui-illustration [name]="s.illustration" [size]="40" />
        }
        <div class="sec-meta">
          <!-- 3.23 (U32): whose application this is — an eyebrow line above
               every step title (the subtitle stays "<group> · Step N of M"). -->
          @if (eyebrow()) {
            <div class="sec-eyebrow">{{ eyebrow() }}</div>
          }
          <div class="sec-title">{{ s.title }}</div>
          <span class="sec-count">
            {{ s.group ? s.group + ' · ' : '' }}Step {{ active() + 1 }} of {{ steps().length }}
          </span>
        </div>
      </div>
    }
    <div class="segbar">
      @for (s of steps(); track s.key; let i = $index) {
        <button
          type="button"
          class="seg"
          [class.ok]="s.state === 'done' && i !== active()"
          [class.cur]="i === active() && s.state !== 'error'"
          [class.warn]="s.state === 'unfinished' && i !== active()"
          [class.err]="s.state === 'error'"
          [attr.aria-current]="i === active() ? 'step' : null"
          [attr.aria-label]="'Step ' + (i + 1) + ' of ' + steps().length + ': ' + s.title"
          (click)="jump.emit(i)"
        ></button>
      }
    </div>

    <ng-content />

    <!-- Host-supplied status (the Application's "Still needed" box) sits last,
         directly above the action bar. -->
    <ng-content select="[step-status]" />

    <ui-action-bar>
      @if (active() > 0) {
        <button
          mat-stroked-button
          type="button"
          secondary
          [disabled]="busy()"
          (click)="back.emit()"
          data-testid="stepper-back"
        >
          Back
        </button>
      }
      <button
        mat-flat-button
        color="primary"
        type="button"
        primary
        [disabled]="busy()"
        [class.next-blocked]="nextDisabled() && !busy()"
        [attr.aria-disabled]="nextDisabled() ? 'true' : null"
        (click)="next.emit()"
        data-testid="stepper-next"
      >
        {{ last() ? lastLabel() : nextLabel() }}
      </button>
    </ui-action-bar>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      /* R38: incomplete step — grey, not a dead control (it still runs the check). */
      .next-blocked {
        opacity: 0.45;
        filter: grayscale(0.4);
        cursor: not-allowed;
      }
      /* the section head card (registration step 2.3, render 04) */
      .sec-head {
        display: flex;
        gap: 12px;
        align-items: center;
        background: var(--gp-tile-sky-bg);
        border: 1.5px solid var(--gp-tile-sky-border);
        border-radius: 20px;
        padding: 10px 12px;
        margin-top: 12px;
      }
      .sec-meta {
        flex: 1;
        min-width: 0;
      }
      .sec-title {
        font-size: 19px;
        font-weight: 800;
        color: var(--gp-ink);
      }
      /* 3.23 (U32): the child's name above the step title. */
      .sec-eyebrow {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.02em;
        color: var(--gp-ink-muted);
      }
      .sec-count {
        font-size: 14px;
        font-weight: 600;
        color: var(--gp-ink-muted);
      }
      /* the segment bar: an 8px bar per step with a 24px tap target; the
         ok / cur / warn / err classes are mutually exclusive (err > cur,
         ok/warn only show on the segments the parent is not on). */
      .segbar {
        display: flex;
        gap: 4px;
        margin: 2px 2px 0;
      }
      .seg {
        flex: 1;
        height: 24px;
        padding: 8px 0;
        border: none;
        background: transparent;
        cursor: pointer;
      }
      .seg::before {
        content: '';
        display: block;
        height: 8px;
        border-radius: 99px;
        background: #e9e1d2;
      }
      .seg.ok::before {
        background: #7db07a;
      }
      .seg.cur::before {
        background: var(--gp-secondary);
      }
      /* U12 (round 3): unfinished — the kit's amber "pending" tone. */
      .seg.warn::before {
        background: var(--gp-pending);
      }
      .seg.err::before {
        background: var(--gp-danger);
      }
    `,
  ],
})
export class UiStepperComponent {
  readonly steps = input.required<UiStepperStep[]>();
  /** The step on screen; the host moves it (the stepper only emits). */
  readonly active = model(0);
  readonly busy = input(false);
  readonly nextLabel = input('Save & continue');
  /**
   * 3.23 (U32): an optional small line above the step title — the Application
   * passes "<Child first> <Child last>'s Application"; empty hides it.
   */
  readonly eyebrow = input('');
  /**
   * R38 (4.3): the primary button LOOKS disabled while the step is incomplete,
   * but stays clickable — pressing it is what reveals the missing answers.
   */
  readonly nextDisabled = input(false);
  readonly lastLabel = input('Review');

  readonly next = output<void>();
  readonly back = output<void>();
  readonly jump = output<number>();

  /** The active step's descriptor (null when the index is out of bounds). */
  readonly step = computed(() => this.steps()[this.active()] ?? null);
  /** True on the last step — the primary button then shows lastLabel. */
  readonly last = computed(() => this.active() >= this.steps().length - 1);
}
